import Calendar from 'react-calendar';
import styled, { css } from 'styled-components';
import { pxToRem } from 'utils/convert';
import { colors, spacing, heading4, fontWeights, body1, fontSizes, body1Bold } from 'ui/theme';
import type { CalendarProps } from 'react-calendar';

/** slight background coloring shown on the background of the range start selected date on the date range calendar */
const rangeStartBackground = css`
  ::after {
    background-color: ${colors.purple10};
    content: '';
    inset: 0 50%;
    position: absolute;
    right: -50%;
    z-index: -2;
  }
`;

/** slight background coloring shown on the background of the range end selected date on the date range calendar */
const rangeEndBackground = css`
  ::after {
    background-color: ${colors.purple10};
    content: '';
    inset: 0 50%;
    left: -50%;
    position: absolute;
    z-index: -2;
  }
`;

/** the round purple indicator visible on the selected date(s) */
const rangeSelector = css`
  ::before {
    content: '';
    background-color: ${colors.purple100};
    border-radius: 100%;
    inset: 0 ${pxToRem(4)};
    position: absolute;
  }
`;

export const StyledCalendar = styled(Calendar)<{
  showNeighboringMonth: CalendarProps['showNeighboringMonth'];
}>`
  border: none;
  width: ${pxToRem(352)};
  margin-left: ${spacing.threeQuarter};
  .react-calendar__navigation {
    align-items: center;
    display: flex;
    gap: ${spacing.half};
    margin-left: ${pxToRem(10)};
    padding-bottom: ${spacing.base};
    padding-top: ${spacing.base};
  }
  .react-calendar__navigation__label {
    all: unset;
    ${heading4};
    color: ${colors.textPrimary};
    font-weight: ${fontWeights.medium};
    order: 0;
  }
  .react-calendar__navigation__arrow {
    all: unset;
    padding: ${spacing.half};
    &:hover {
      background: rgb(245, 244, 244);
    }
  }
  .react-calendar__navigation__prev-button {
    order: 1;
  }
  .react-calendar__navigation__next-button {
    order: 2;
  }
  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }
  .react-calendar__month-view__weekdays {
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: ${spacing.half};
  }
  .react-calendar__month-view__weekdays__weekday {
    ${body1}
    abbr {
      text-decoration: none;
    }
  }
  .react-calendar__month-view__days__day {
    all: unset;
    ${body1}
    box-sizing: border-box;
    cursor: pointer;
    font-size: ${fontSizes.s16};
    margin-bottom: ${spacing.quarter};
    height: ${pxToRem(44)};
    position: relative;
    text-align: center;
    &:disabled {
      color: ${colors.grey35};
    }
    abbr {
      display: none;
    }
    div {
      position: absolute;
      inset: ${pxToRem(10)};
    }
  }
  .react-calendar__tile--rangeStart,
  .react-calendar__tile--rangeEnd {
    ${body1Bold}
    color: ${colors.white};
    position: relative;
    ${rangeSelector}
  }

  .react-calendar__tile--active:not(.react-calendar__tile--rangeBothEnds):not(.react-calendar__tile--rangeStart):not(.react-calendar__tile--rangeEnd) {
    background-color: ${colors.purple10};
    position: relative;
  }
  .react-calendar__tile--hasActive:not(.react-calendar__tile--active):not(.react-calendar__tile--rangeBothEnds):not(.react-calendar__tile--rangeStart):not(.react-calendar__tile--rangeEnd) {
    position: relative;
    color: ${colors.white};
    ${rangeSelector}
  }
  .react-calendar__tile--rangeStart:not(.react-calendar__tile--rangeBothEnds) {
    ${rangeStartBackground}
  }
  .react-calendar__tile--rangeEnd:not(.react-calendar__tile--rangeBothEnds) {
    ${rangeEndBackground}
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    visibility: ${({ showNeighboringMonth }) => (showNeighboringMonth ? 'inherit' : 'hidden')};
  }
`;

export const StyledIcon = styled.div`
  && {
    path {
      stroke: ${colors.textSecondary};
    }
  }
  &:hover {
    cursor: pointer;
  }
`;

export const StyledCoreCalendar = styled(Calendar)<{
  showNeighboringMonth: CalendarProps['showNeighboringMonth'];
}>`
  border: none;
  width: ${pxToRem(352)};
  padding: ${spacing.half};
  .react-calendar__navigation {
    align-items: center;
    display: flex;
    gap: ${spacing.half};
    justify-content: center;
    padding-bottom: ${spacing.base};
  }
  .react-calendar__navigation__label {
    all: unset;
    color: ${colors.textPrimary};
    flex: 0 0 50%;
    order: 1;
    text-align: center;
    ${heading4}
    font-weight: ${fontWeights.medium};
  }
  .react-calendar__navigation__arrow {
    all: unset;
    padding: ${spacing.half};
    &:hover {
      background: rgb(245, 244, 244);
    }
  }
  .react-calendar__navigation__prev-button {
    order: 1;
    margin-left: ${spacing.quarter};
  }
  .react-calendar__navigation__next-button {
    order: 2;
    margin-right: ${spacing.quarter};
  }
  .react-calendar__navigation__prev2-button,
  .react-calendar__navigation__next2-button {
    display: none;
  }
  .react-calendar__month-view__weekdays {
    text-align: center;
    margin-bottom: ${spacing.half};
  }
  .react-calendar__month-view__weekdays__weekday {
    ${body1}
    abbr {
      color: ${colors.grey60};
      text-decoration: none;
      pointer-events: none;
    }
  }
  .react-calendar__month-view__days__day {
    all: unset;
    ${body1}
    box-sizing: border-box;
    cursor: pointer;
    padding: ${pxToRem(22)};
    font-size: ${fontSizes.s16};
    position: relative;
    text-align: center;
    &:hover {
      background-color: rgb(234, 244, 250);
      color: #2e8ece;
    }
    &:disabled {
      background-color: #eff0f0;
      color: #444;
    }
    abbr {
      display: none;
    }
    div {
      position: absolute;
      inset: ${pxToRem(10)};
    }
  }
  ${StyledIcon} {
    height: ${pxToRem(20)};
    svg {
      path {
        stroke: ${colors.black};
      }
    }
  }
  .react-calendar__tile--active:not(.react-calendar__tile--range) {
    color: ${colors.white};
    background-color: #79b7e0;
  }
  .react-calendar__tile--range {
    background-color: rgb(234, 244, 250);
  }
  .react-calendar__tile--rangeStart,
  .react-calendar__tile--rangeEnd {
    color: ${colors.white};
    background-color: #79b7e0;
  }
  .react-calendar__month-view__days__day--neighboringMonth {
    visibility: ${({ showNeighboringMonth }) => (showNeighboringMonth ? 'inherit' : 'hidden')};
  }
`;
